import {
    homeCrossRegionalAdvertsContainer,
    homeCrossRegionalAdvertsSection,
    homeRegionalAdvertsContainer,
    homeRegionalAdvertsSection,
    homeShowMoreCrossRegionalAdvertsButton,
    homeShowMoreRegionalAdvertsButton,
} from "@modules/global/dom"
import { initAnnouncements } from "./home-announcements.ts"

const loadItemCount = (): number => {
    return 15
}

const regionalItemsPerRow = (): number => {
    if (window.innerWidth >= 1200) {
        return 5
    }
    if (window.innerWidth >= 992) {
        return 4
    }
    return 3
}

const regionalRowsPerClickCount = (): number => {
    return Math.ceil(loadItemCount() / regionalItemsPerRow())
}

export const scrollPageTo = (to: string | number | HTMLElement, duration = 500): Promise<void> => {
    //t = current time
    //b = start value
    //c = change in value
    //d = duration
    const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
        let timeFactor = t / (d / 2)
        if (timeFactor < 1) return (c / 2) * timeFactor * timeFactor + b
        timeFactor--
        return (-c / 2) * (timeFactor * (timeFactor - 2) - 1) + b
    }

    return new Promise((resolve, reject) => {
        const scrollingElement: Element | null = document.scrollingElement
        if (!scrollingElement) {
            reject()
            return
        }

        let offsetTop: any = to
        if (typeof offsetTop === "string") {
            offsetTop = document.querySelector(offsetTop)
            if (!offsetTop) {
                reject()
                return
            }
        }
        if (offsetTop instanceof HTMLElement) {
            offsetTop = offsetTop.getBoundingClientRect().top + scrollingElement.scrollTop
        } else {
            offsetTop = to
        }
        if (typeof offsetTop !== "number") {
            reject()
            return
        }

        const start = scrollingElement.scrollTop
        const change = offsetTop - start
        let currentTime = 0
        const increment = 20

        const animateScroll = () => {
            currentTime += increment
            scrollingElement.scrollTop = easeInOutQuad(currentTime, start, change, duration)
            if (currentTime < duration) {
                setTimeout(animateScroll, increment)
            } else {
                resolve()
            }
        }
        animateScroll()
    })
}

const isAllRegionHome = (): boolean => {
    const crossRegionalAdvertsContainer = homeCrossRegionalAdvertsContainer()
    if (!crossRegionalAdvertsContainer) {
        return false
    }

    return !!crossRegionalAdvertsContainer.closest(".page-stripe")?.classList.contains("lighter-steel-blue")
}

const hideCrossRegionalAdverts = () => {
    const gutter = 20
    const showingContent = Number.parseInt(sessionStorage.getItem("openCrossRegionalAdverts") ?? "0")
    const crossRegionalAdvertsContainer = homeCrossRegionalAdvertsContainer()
    const cardHeight = crossRegionalAdvertsContainer?.querySelector(".ladies-card")?.clientHeight
    if (!crossRegionalAdvertsContainer || !cardHeight) {
        return
    }
    const rows = isAllRegionHome() ? 3 : 1
    crossRegionalAdvertsContainer.dataset.showingAll = "false"
    crossRegionalAdvertsContainer.style.transition = "none"
    crossRegionalAdvertsContainer.style.overflow = "show"
    crossRegionalAdvertsContainer.style.height = "auto"
    const maxHeight = crossRegionalAdvertsContainer.clientHeight
    crossRegionalAdvertsContainer.dataset.maxHeight = maxHeight.toString()
    const rowHeight = (cardHeight + gutter) * rows
    crossRegionalAdvertsContainer.style.overflow = "hidden"
    if (showingContent === 0) {
        crossRegionalAdvertsContainer.style.height = `${rowHeight}px`
        return
    }
    crossRegionalAdvertsContainer.dataset.showingAll = "true"
    const section = homeCrossRegionalAdvertsSection()
    if (section) {
        section.classList.add("more-is-open")
    }
}

const showMoreCrossRegionalAdverts = (e: Event) => {
    e.preventDefault()
    const gutter = 20
    const crossRegionalAdvertsContainer = homeCrossRegionalAdvertsContainer()
    const cardHeight = crossRegionalAdvertsContainer?.querySelector(".ladies-card")?.clientHeight
    const section = homeCrossRegionalAdvertsSection()
    if (!crossRegionalAdvertsContainer || !cardHeight || !section) {
        return
    }
    const rows = isAllRegionHome() ? 3 : 1
    crossRegionalAdvertsContainer.style.transition = "height 0.5s"
    const maxHeight = Number.parseInt(crossRegionalAdvertsContainer.dataset.maxHeight ?? "0")
    const rowHeight = (cardHeight + gutter) * rows
    if (crossRegionalAdvertsContainer.dataset.showingAll === "true") {
        crossRegionalAdvertsContainer.style.height = `${rowHeight}px`
        scrollPageTo(section)
        sessionStorage.setItem("openCrossRegionalAdverts", "0")
        crossRegionalAdvertsContainer.dataset.showingAll = "false"
        section.classList.remove("more-is-open")
        return
    }
    sessionStorage.setItem("openCrossRegionalAdverts", "1")
    crossRegionalAdvertsContainer.style.height = `${maxHeight.toString()}px`
    crossRegionalAdvertsContainer.dataset.showingAll = "true"
    section.classList.add("more-is-open")
}

let crossRegionalAdvertsTimeout: Timer | number = 0
const setTimeoutForHideCrossRegionalAdverts = () => {
    clearTimeout(crossRegionalAdvertsTimeout)
    crossRegionalAdvertsTimeout = setTimeout(hideCrossRegionalAdverts, 50)
}

document.addEventListener("DOMContentLoaded", hideCrossRegionalAdverts)
homeShowMoreCrossRegionalAdvertsButton()?.addEventListener("click", showMoreCrossRegionalAdverts)
window.addEventListener("resize", setTimeoutForHideCrossRegionalAdverts)

const hideRegionalAdverts = () => {
    const gutter = 20
    const showingContent = Number.parseInt(sessionStorage.getItem("openRegionalAdverts") ?? "0")
    const regionalAdvertsContainer = homeRegionalAdvertsContainer()
    const cardHeight = regionalAdvertsContainer?.querySelector(".ladies-card")?.clientHeight
    if (!regionalAdvertsContainer || !cardHeight) {
        return
    }
    regionalAdvertsContainer.dataset.showingAll = "false"
    regionalAdvertsContainer.style.transition = "none"
    regionalAdvertsContainer.style.overflow = "show"
    regionalAdvertsContainer.style.height = "auto"
    const maxHeight = regionalAdvertsContainer.clientHeight
    regionalAdvertsContainer.dataset.maxHeight = maxHeight.toString()
    const rowHeight = cardHeight + gutter
    regionalAdvertsContainer.style.overflow = "hidden"
    const minHeight = Math.min(regionalAdvertsContainer.clientHeight, regionalRowsPerClickCount() * rowHeight)
    if (showingContent === 0) {
        regionalAdvertsContainer.style.height = `${minHeight}px`
        return
    }
    regionalAdvertsContainer.dataset.showingAll = "true"
    const section = homeRegionalAdvertsSection()
    if (section) {
        section.classList.add("more-is-open")
    }
}

const showMoreRegionalAdverts = (e: Event) => {
    e.preventDefault()
    const gutter = 20
    const regionalAdvertsContainer = homeRegionalAdvertsContainer()
    const cardHeight = regionalAdvertsContainer?.querySelector(".ladies-card")?.clientHeight
    const section = homeRegionalAdvertsSection()
    if (!regionalAdvertsContainer || !cardHeight || !section) {
        return
    }
    regionalAdvertsContainer.style.transition = "height 0.5s"
    const maxHeight = Number.parseInt(regionalAdvertsContainer.dataset.maxHeight ?? "0")
    const rowHeight = cardHeight + gutter
    const minHeight = Math.min(regionalAdvertsContainer.clientHeight, regionalRowsPerClickCount() * rowHeight)
    if (regionalAdvertsContainer.dataset.showingAll === "true") {
        regionalAdvertsContainer.style.height = `${minHeight}px`
        scrollPageTo(section)
        sessionStorage.setItem("openRegionalAdverts", "0")
        regionalAdvertsContainer.dataset.showingAll = "false"
        section.classList.remove("more-is-open")
        return
    }
    sessionStorage.setItem("openRegionalAdverts", "1")
    regionalAdvertsContainer.style.height = `${maxHeight.toString()}px`
    regionalAdvertsContainer.dataset.showingAll = "true"
    section.classList.add("more-is-open")
}

let regionalAdvertsTimeout: Timer | number = 0
const setTimeoutForHideRegionalAdverts = () => {
    clearTimeout(regionalAdvertsTimeout)
    regionalAdvertsTimeout = setTimeout(hideRegionalAdverts, 50)
}

document.addEventListener("DOMContentLoaded", hideRegionalAdverts)
document.addEventListener("DOMContentLoaded", initAnnouncements)
homeShowMoreRegionalAdvertsButton()?.addEventListener("click", showMoreRegionalAdverts)
window.addEventListener("resize", setTimeoutForHideRegionalAdverts)
